// BOORO: the dashboard in the Start area: what the team delivered, in a period,
// over the offices the filter shows.
import { formatCost, formatSavedMinutes, formatSuccessRate, periodChange } from "@booro/tasks";
import type { OrgScope } from "@booro/team";
import { agentInScope } from "@booro/team";
import { Trans, useLingui } from "@lingui/react/macro";
import type {
  DashboardPeriod,
  DashboardPoint,
  DashboardStats,
  OrgSnapshot,
} from "@rakazo/contracts";
import {
  BotAvatar,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@rakazo/ui-web";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { ModelBreakdown } from "../../components/ModelBreakdown";
import { rpc } from "../../lib/rpc";
import { errorText } from "../../lib/user-error";

/** How often the dashboard reloads while it is open. */
const REFRESH_MS = 10_000;

/** What the numbers cover: every office, one office or the agents of the filter. */
type DashboardFilter = { spaceIds?: string[]; botIds?: string[] };

/** The numbers follow the same office, group or agent filter as the inbox. */
function dashboardFilter(snapshot: OrgSnapshot | null, scope: OrgScope): DashboardFilter {
  if (!snapshot) return {};
  const filter: DashboardFilter = {};
  if (scope.spaceId) filter.spaceIds = [scope.spaceId];
  if (scope.botId) filter.botIds = [scope.botId];
  else if (scope.groupId) {
    filter.botIds = snapshot.agents
      .filter((agent) => agentInScope(snapshot.groups, agent, scope))
      .map((agent) => agent.id);
  }
  return filter;
}

function usePeriodLabels(): Record<DashboardPeriod, string> {
  const { t } = useLingui();
  return {
    "7d": t`7 days`,
    "30d": t`30 days`,
    "90d": t`90 days`,
    month: t`This month`,
  };
}

type Tile = {
  key: string;
  label: string;
  value: string;
  change: { direction: "up" | "down" | "flat"; percent: number | null };
  /** A change down is good for costs, so the arrow points the other way. */
  lowerIsBetter?: boolean;
};

export function DashboardView({
  snapshot,
  scope,
}: {
  snapshot: OrgSnapshot | null;
  scope: OrgScope;
}) {
  const { t } = useLingui();
  const [period, setPeriod] = useState<DashboardPeriod>("30d");
  const [loaded, setLoaded] = useState<{ key: string; stats: DashboardStats } | null>(null);
  const [error, setError] = useState("");
  const [sharing, setSharing] = useState(false);
  const periodLabels = usePeriodLabels();
  const filter = useMemo(() => dashboardFilter(snapshot, scope), [snapshot, scope]);
  // A stable key per filter, so a re-render does not reload the numbers; an
  // empty agent list is a filter of its own and must not fall back to all.
  const spaceIdsKey = filter.spaceIds?.join(",") ?? "";
  const botIdsKey = filter.botIds ? filter.botIds.join(",") : null;
  const query = useMemo(
    () => ({
      ...(spaceIdsKey ? { spaceIds: spaceIdsKey.split(",") } : {}),
      ...(botIdsKey !== null ? { botIds: botIdsKey ? botIdsKey.split(",") : [] } : {}),
    }),
    [spaceIdsKey, botIdsKey],
  );
  // The numbers stay with the period and filter they were read for, so another
  // office's figures never stand under this filter.
  const key = `${period}|${spaceIdsKey}|${botIdsKey ?? "-"}`;
  const stats = loaded?.key === key ? loaded.stats : null;

  useEffect(() => {
    let active = true;
    const load = () =>
      rpc.booroDashboard
        .stats({
          period,
          bucket: period === "90d" ? "week" : "day",
          ...query,
        })
        .then((next) => {
          if (active) {
            setLoaded({ key, stats: next });
            setError("");
          }
        })
        .catch((cause) => {
          if (active) setError(errorText(cause));
        });
    void load();
    const timer = window.setInterval(() => void load(), REFRESH_MS);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [period, query, key]);

  const tiles = useMemo<Tile[]>(() => {
    if (!stats) return [];
    return [
      {
        key: "done",
        label: t`Tasks done`,
        value: String(stats.done),
        change: periodChange(stats.done, stats.previous.done),
      },
      {
        key: "rate",
        label: t`Success rate`,
        value: formatSuccessRate(stats.successRate),
        change: periodChange(stats.successRate, stats.previous.successRate),
      },
      {
        key: "saved",
        label: t`Time saved`,
        value: formatSavedMinutes(stats.minutesSaved),
        change: periodChange(stats.minutesSaved, stats.previous.minutesSaved),
      },
      {
        key: "cost",
        label: t`Cost`,
        value: formatCost(stats.costUsd),
        change: periodChange(stats.costUsd, stats.previous.costUsd),
        lowerIsBetter: true,
      },
    ];
  }, [stats, t]);

  if (error) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {error}
      </p>
    );
  }
  if (!stats) {
    return <div className="h-40" aria-hidden="true" />;
  }
  // No task was created in the period: a sentence instead of empty charts.
  const empty = stats.done === 0 && stats.agents.length === 0;

  return (
    <section data-testid="dashboard" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={period} onValueChange={(value) => setPeriod(value as DashboardPeriod)}>
          <TabsList aria-label={t`Period`}>
            {(["7d", "30d", "90d", "month"] as const).map((option) => (
              <TabsTrigger key={option} value={option}>
                {periodLabels[option]}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {empty ? null : (
          <Button size="sm" variant="outline" onClick={() => setSharing(true)}>
            <Trans>Share</Trans>
          </Button>
        )}
      </div>

      {sharing ? (
        <ShareDialog period={period} filter={filter} onClose={() => setSharing(false)} />
      ) : null}

      {empty ? (
        <div className="rounded-2xl border border-border bg-card px-5 py-6">
          <p className="text-[15px] text-foreground">
            <Trans>No tasks in this period yet.</Trans>
          </p>
          <p className="mt-1 text-[13.5px] text-muted-foreground">
            <Trans>Create a task and the numbers appear here.</Trans>
          </p>
        </div>
      ) : (
        <DashboardNumbers stats={stats} tiles={tiles} />
      )}
    </section>
  );
}

function DashboardNumbers({ stats, tiles }: { stats: DashboardStats; tiles: Tile[] }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {tiles.map((tile) => (
          <div
            key={tile.key}
            data-testid={`dashboard-tile-${tile.key}`}
            className="rounded-2xl border border-border bg-card px-4 py-3"
          >
            <p className="text-[12.5px] text-muted-foreground">{tile.label}</p>
            <p className="mt-1 text-[22px] font-semibold text-foreground">{tile.value}</p>
            <ChangeLabel change={tile.change} lowerIsBetter={tile.lowerIsBetter} />
          </div>
        ))}
      </div>

      <Timeline points={stats.series} />

      <div className="overflow-x-auto rounded-2xl border border-border bg-card">
        <table className="w-full min-w-[480px] text-[13.5px]">
          <thead>
            <tr className="border-b border-border text-start text-[12px] tracking-wider uppercase text-muted-foreground">
              <th className="px-4 py-2.5 text-start font-medium">
                <Trans>Agent</Trans>
              </th>
              <th className="px-4 py-2.5 text-end font-medium">
                <Trans>Done</Trans>
              </th>
              <th className="px-4 py-2.5 text-end font-medium">
                <Trans>Success rate</Trans>
              </th>
              <th className="px-4 py-2.5 text-end font-medium">
                <Trans>Time saved</Trans>
              </th>
              <th className="px-4 py-2.5 text-end font-medium">
                <Trans>Cost</Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {stats.agents.map((agent) => (
              <tr key={agent.botId} className="border-b border-border last:border-b-0">
                <td className="px-4 py-2.5">
                  <span className="flex items-center gap-2">
                    <BotAvatar color={agent.botColor} identity={agent.botId} size={20} />
                    <span className="truncate" dir="auto">
                      {agent.botName}
                    </span>
                  </span>
                </td>
                <td className="px-4 py-2.5 text-end">{agent.done}</td>
                <td className="px-4 py-2.5 text-end">{formatSuccessRate(agent.successRate)}</td>
                <td className="px-4 py-2.5 text-end">{formatSavedMinutes(agent.minutesSaved)}</td>
                <td className="px-4 py-2.5 text-end">{formatCost(agent.costUsd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ModelBreakdown models={stats.models} />
    </>
  );
}

/** Shares the numbers of a period as a link, and takes links back. */
function ShareDialog({
  period,
  filter,
  onClose,
}: {
  period: DashboardPeriod;
  filter: DashboardFilter;
  onClose: () => void;
}) {
  const { t } = useLingui();
  const [links, setLinks] = useState<{ id: string; from: string; to: string; url: string }[]>([]);
  const [created, setCreated] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const absolute = (url: string) => `${window.location.origin}${url}`;

  const loadLinks = async () => {
    try {
      setLinks(await rpc.booroDashboard.shares());
    } catch (cause) {
      setError(errorText(cause));
    }
  };
  useEffect(() => {
    void loadLinks();
  }, []);

  async function create() {
    setBusy(true);
    setError("");
    try {
      const share = await rpc.booroDashboard.share({ period, ...filter });
      setCreated(share.id);
      await loadLinks();
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(absolute(url));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  async function revoke(shareId: string) {
    setBusy(true);
    setError("");
    try {
      await rpc.booroDashboard.revokeShare({ shareId });
      if (created === shareId) setCreated(null);
      await loadLinks();
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(false);
    }
  }

  const createdLink = links.find((link) => link.id === created);
  const others = links.filter((link) => link.id !== created);

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Trans>Share numbers</Trans>
          </DialogTitle>
        </DialogHeader>
        <p className="text-[13.5px] text-muted-foreground">
          <Trans>
            Anyone with the link sees the numbers of this period, without tasks or names.
          </Trans>
        </p>
        {createdLink ? (
          <div className="flex items-center gap-2">
            <Input
              readOnly
              aria-label={t`Link`}
              value={absolute(createdLink.url)}
              onFocus={(event) => event.target.select()}
            />
            <Button size="sm" onClick={() => void copy(createdLink.url)}>
              {copied ? <Trans>Copied</Trans> : <Trans>Copy</Trans>}
            </Button>
          </div>
        ) : (
          <Button className="self-start" disabled={busy} onClick={() => void create()}>
            <Trans>Create link</Trans>
          </Button>
        )}
        {others.length > 0 ? (
          <div className="space-y-1.5">
            <p className="text-[12.5px] text-muted-foreground">
              <Trans>Shared links</Trans>
            </p>
            {others.map((link) => (
              <div key={link.id} className="flex items-center justify-between gap-2 text-[13.5px]">
                <span className="truncate">
                  {new Date(link.from).toLocaleDateString()} –{" "}
                  {new Date(link.to).toLocaleDateString()}
                </span>
                <span className="flex shrink-0 gap-1">
                  <Button size="sm" variant="ghost" onClick={() => void copy(link.url)}>
                    <Trans>Copy</Trans>
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => void revoke(link.id)}
                  >
                    <Trans>Revoke</Trans>
                  </Button>
                </span>
              </div>
            ))}
          </div>
        ) : null}
        {createdLink ? (
          <Button
            className="self-start"
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => void revoke(createdLink.id)}
          >
            <Trans>Revoke link</Trans>
          </Button>
        ) : null}
        {error ? (
          <p role="alert" className="text-[13px] text-destructive">
            {error}
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function ChangeLabel({
  change,
  lowerIsBetter,
}: {
  change: { direction: "up" | "down" | "flat"; percent: number | null };
  lowerIsBetter?: boolean;
}) {
  const { t } = useLingui();
  if (change.percent === null) {
    return <p className="mt-1 text-[12.5px] text-muted-foreground">—</p>;
  }
  const good = lowerIsBetter ? change.direction === "down" : change.direction === "up";
  const Icon =
    change.direction === "up" ? ArrowUpRight : change.direction === "down" ? ArrowDownRight : Minus;
  return (
    <p
      className={`mt-1 flex items-center gap-1 text-[12.5px] ${
        change.direction === "flat"
          ? "text-muted-foreground"
          : good
            ? "text-success"
            : "text-destructive"
      }`}
    >
      <Icon size={13} strokeWidth={2} aria-hidden="true" />
      {change.percent}%
      <span className="sr-only">
        {change.direction === "up"
          ? t`higher than the period before`
          : change.direction === "down"
            ? t`lower than the period before`
            : t`unchanged`}
      </span>
    </p>
  );
}

/** The tasks done per day, as bars. */
function Timeline({ points }: { points: DashboardPoint[] }) {
  if (points.length === 0) return null;
  const max = Math.max(...points.map((point) => point.done), 1);
  return (
    <div
      data-testid="dashboard-timeline"
      className="rounded-2xl border border-border bg-card px-4 py-3"
    >
      <p className="mb-2 text-[12.5px] text-muted-foreground">
        <Trans>Tasks done over time</Trans>
      </p>
      <div className="flex h-24 items-end gap-1">
        {points.map((point) => (
          <div
            key={point.start}
            title={`${new Date(point.start).toLocaleDateString()}: ${point.done}`}
            className="flex-1 rounded-t bg-primary/70"
            style={{ height: `${point.done === 0 ? 2 : Math.max(8, (point.done / max) * 100)}%` }}
          />
        ))}
      </div>
    </div>
  );
}
